import { useState, useEffect, useCallback } from "react";
import { useOutletContext } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { 
  Video, 
  Loader2,
  Play,
  Download,
  FileText,
  Film,
  Clock,
  Sparkles,
  AlertCircle,
  Zap,
  ChevronDown,
  RefreshCw
} from "lucide-react";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { ScrollArea } from "../components/ui/scroll-area";
import { Skeleton } from "../components/ui/skeleton";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "../components/ui/dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "../components/ui/dropdown-menu";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function VideoStudio() {
  const { darkMode, region, brand } = useOutletContext() || { darkMode: true, region: 'UAE', brand: 'blackswan' };
  const [videos, setVideos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedVideo, setSelectedVideo] = useState(null);
  const [generatingIds, setGeneratingIds] = useState(new Set());
  const [previewOpen, setPreviewOpen] = useState(false);
  const [batchGenerating, setBatchGenerating] = useState(false);
  const [batchJob, setBatchJob] = useState(null);
  const [pendingCount, setPendingCount] = useState(0);

  const fetchVideos = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/videos?region=${region}&brand=${brand}`);
      setVideos(res.data);
      // Calculate pending count
      const pending = res.data.filter(v => v.status === 'pending').length;
      setPendingCount(pending);
    } catch (error) {
      toast.error("Failed to load videos");
    } finally {
      setLoading(false);
    }
  }, [region, brand]);

  useEffect(() => {
    setLoading(true);
    fetchVideos();
  }, [fetchVideos, region]);

  // Poll for batch job status
  useEffect(() => {
    if (!batchJob) return;
    
    const pollInterval = setInterval(async () => {
      try {
        const res = await axios.get(`${API}/batch/video-jobs/${batchJob.job_id}/status`);
        if (res.data.status === "completed") {
          clearInterval(pollInterval);
          setBatchGenerating(false);
          setBatchJob(null);
          fetchVideos();
          toast.success(`Batch complete: ${res.data.completed_videos} videos generated, ${res.data.failed_videos} failed`);
        } else {
          // Update progress
          setBatchJob(prev => ({...prev, ...res.data}));
        }
      } catch (e) {
        console.error("Error polling batch job:", e);
      }
    }, 15000); // Poll every 15 seconds
    
    return () => clearInterval(pollInterval);
  }, [batchJob, fetchVideos]);

  const generateVideo = async (videoId) => {
    setGeneratingIds(prev => new Set([...prev, videoId]));
    try {
      await axios.post(`${API}/videos/${videoId}/generate`);
      toast.success("Video generation started! This may take a few minutes.");
      
      const pollInterval = setInterval(async () => {
        try {
          const res = await axios.get(`${API}/videos/${videoId}`);
          if (res.data.status === "completed" || res.data.status === "failed") {
            clearInterval(pollInterval);
            setGeneratingIds(prev => {
              const next = new Set(prev);
              next.delete(videoId);
              return next;
            });
            fetchVideos();
            if (res.data.status === "completed") {
              toast.success("Video generated successfully!");
            } else {
              toast.error("Video generation failed");
            }
          }
        } catch (e) {
          // Polling failure — log but keep polling silently to avoid spamming user
          console.debug("Video status poll failed:", e?.message || e);
        }
      }, 10000);
    } catch (error) {
      setGeneratingIds(prev => {
        const next = new Set(prev);
        next.delete(videoId);
        return next;
      });
      toast.error("Failed to start video generation");
    }
  };

  const startBatchGeneration = async (limit = 5) => {
    setBatchGenerating(true);
    try {
      const res = await axios.post(`${API}/batch/generate-videos/${brand}/${region}?limit=${limit}`);
      if (res.data.queued > 0) {
        setBatchJob(res.data);
        toast.success(`Started batch generation for ${res.data.queued} videos`);
        fetchVideos();
      } else {
        toast.info("No pending videos to generate");
        setBatchGenerating(false);
      }
    } catch (error) {
      toast.error("Failed to start batch generation");
      setBatchGenerating(false);
    }
  };

  const regenerateVideo = async (videoId) => {
    setGeneratingIds(prev => new Set([...prev, videoId]));
    try {
      await axios.post(`${API}/videos/${videoId}/regenerate`);
      toast.success("Video regeneration started! All scenes will be recreated with consistent style.");
      
      // Poll for completion
      const pollInterval = setInterval(async () => {
        try {
          const res = await axios.get(`${API}/videos/${videoId}`);
          if (res.data.status === "completed" || res.data.status === "failed") {
            clearInterval(pollInterval);
            setGeneratingIds(prev => {
              const next = new Set(prev);
              next.delete(videoId);
              return next;
            });
            fetchVideos();
            if (res.data.status === "completed") {
              toast.success("Video regenerated successfully!");
            } else {
              toast.error("Video regeneration failed");
            }
          }
        } catch (e) {
          // Polling failure — log but keep polling silently
          console.debug("Video regen status poll failed:", e?.message || e);
        }
      }, 15000);
    } catch (error) {
      setGeneratingIds(prev => {
        const next = new Set(prev);
        next.delete(videoId);
        return next;
      });
      toast.error("Failed to start video regeneration");
    }
  };

  const getStatusBadge = (status) => {
    const styles = {
      pending: "bg-amber-100 text-amber-700 border-0",
      generating: "bg-blue-100 text-blue-700 border-0",
      completed: "bg-emerald-100 text-emerald-700 border-0",
      failed: "bg-red-100 text-red-700 border-0",
    };
    return styles[status] || styles.pending;
  };

  const openPreview = (video) => {
    setSelectedVideo(video);
    setPreviewOpen(true);
  };

  if (loading) {
    return (
      <div className="max-w-7xl mx-auto space-y-6" data-testid="video-loading">
        {/* Header Skeleton */}
        <div className="flex items-center justify-between animate-fadeIn">
          <div>
            <Skeleton className={`h-8 w-36 mb-2 ${darkMode ? 'bg-[#21262d]' : ''}`} />
            <Skeleton className={`h-4 w-72 ${darkMode ? 'bg-[#21262d]' : ''}`} />
          </div>
          <Skeleton className={`h-6 w-20 rounded-full ${darkMode ? 'bg-[#21262d]' : ''}`} />
        </div>
        {/* Video Cards Grid Skeleton */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {[1, 2, 3, 4, 5, 6].map((i) => (
            <div 
              key={i} 
              className={`rounded-xl overflow-hidden animate-fadeIn stagger-${Math.min(i, 6)} ${darkMode ? 'bg-[#161b22] border border-[#30363d]' : 'bg-white border border-gray-100 shadow-sm'}`}
            >
              {/* Video Preview Skeleton */}
              <Skeleton className={`aspect-video w-full ${darkMode ? 'bg-[#21262d]' : ''}`} />
              {/* Video Info Skeleton */}
              <div className="p-4 space-y-3">
                <Skeleton className={`h-5 w-full ${darkMode ? 'bg-[#21262d]' : ''}`} />
                <Skeleton className={`h-4 w-1/2 ${darkMode ? 'bg-[#21262d]' : ''}`} />
                <div className="flex gap-2">
                  <Skeleton className={`h-10 flex-1 rounded-lg ${darkMode ? 'bg-[#21262d]' : ''}`} />
                  <Skeleton className={`h-10 w-10 rounded-lg ${darkMode ? 'bg-[#21262d]' : ''}`} />
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto space-y-6" data-testid="video-studio-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className={`text-2xl font-semibold ${darkMode ? 'text-white' : 'text-gray-900'}`}>
            Video Studio
          </h1>
          <p className={`text-sm mt-1 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
            Generate AI-powered short videos from your content
          </p>
        </div>
        <div className="flex items-center gap-3">
          {/* Batch Generation Button */}
          {pendingCount > 0 && (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button 
                  disabled={batchGenerating}
                  className="bg-[#DC143C] hover:bg-[#b91031] text-white"
                  data-testid="batch-generate-btn"
                >
                  {batchGenerating ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Generating {batchJob?.completed_videos || 0}/{batchJob?.total_videos || 0}...
                    </>
                  ) : (
                    <>
                      <Zap className="w-4 h-4 mr-2" />
                      Generate All ({pendingCount})
                      <ChevronDown className="w-4 h-4 ml-1" />
                    </>
                  )}
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className={darkMode ? 'bg-[#161b22] border-[#30363d]' : ''}>
                <DropdownMenuItem 
                  onClick={() => startBatchGeneration(3)}
                  className={darkMode ? 'text-gray-300 hover:bg-[#21262d]' : ''}
                >
                  <Sparkles className="w-4 h-4 mr-2" />
                  Generate 3 videos
                </DropdownMenuItem>
                <DropdownMenuItem 
                  onClick={() => startBatchGeneration(5)}
                  className={darkMode ? 'text-gray-300 hover:bg-[#21262d]' : ''}
                >
                  <Sparkles className="w-4 h-4 mr-2" />
                  Generate 5 videos
                </DropdownMenuItem>
                <DropdownMenuItem 
                  onClick={() => startBatchGeneration(10)}
                  className={darkMode ? 'text-gray-300 hover:bg-[#21262d]' : ''}
                >
                  <Zap className="w-4 h-4 mr-2" />
                  Generate 10 videos
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          )}
          <Badge className="bg-[#DC143C]/10 text-[#DC143C] border-0">
            {videos.length} videos
          </Badge>
        </div>
      </div>

      {/* Empty State */}
      {videos.length === 0 ? (
        <div className={`rounded-xl p-16 text-center ${darkMode ? 'bg-[#161b22] border border-[#30363d] text-gray-400' : 'bg-white border border-gray-100 shadow-sm text-gray-500'}`} data-testid="empty-state">
          <Video className="w-12 h-12 mx-auto mb-4 opacity-30" />
          <p className="font-medium">No videos yet</p>
          <p className="text-sm mt-1">Run an intelligence analysis to generate video scripts</p>
        </div>
      ) : (
        /* Videos Grid */
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {videos.map((video, index) => (
            <div 
              key={video.id}
              className={`rounded-xl overflow-hidden ${darkMode ? 'bg-[#161b22] border border-[#30363d]' : 'bg-white border border-gray-100 shadow-sm'}`}
              data-testid={`video-card-${index}`}
            >
              {/* Video Preview */}
              <div className={`relative aspect-video flex items-center justify-center ${darkMode ? 'bg-[#21262d]' : 'bg-gray-100'}`}>
                {video.status === "completed" && video.video_url ? (
                  <video 
                    className="w-full h-full object-cover"
                    onClick={() => openPreview(video)}
                  >
                    <source src={`${process.env.REACT_APP_BACKEND_URL}${video.video_url}`} type="video/mp4" />
                  </video>
                ) : (
                  <div className={`flex flex-col items-center justify-center ${darkMode ? 'text-gray-600' : 'text-gray-400'}`}>
                    <Film className="w-10 h-10 mb-2" />
                    <span className="text-xs">16:9 Landscape</span>
                  </div>
                )}
                
                {video.status === "completed" && video.video_url && (
                  <button 
                    onClick={() => openPreview(video)}
                    className="absolute inset-0 flex items-center justify-center bg-black/50 opacity-0 hover:opacity-100 transition-opacity"
                    data-testid={`play-video-${index}`}
                  >
                    <div className="w-14 h-14 rounded-full bg-[#DC143C] flex items-center justify-center">
                      <Play className="w-6 h-6 text-white ml-1" />
                    </div>
                  </button>
                )}
                
                <div className="absolute top-3 right-3">
                  <Badge className={getStatusBadge(video.status)}>
                    {video.status}
                  </Badge>
                </div>
              </div>

              {/* Video Info */}
              <div className="p-4">
                <h3 className={`font-medium mb-2 line-clamp-2 ${darkMode ? 'text-white' : 'text-gray-900'}`}>{video.topic}</h3>
                
                <div className={`flex items-center gap-4 text-xs mb-3 ${darkMode ? 'text-gray-500' : 'text-gray-400'}`}>
                  <span className="flex items-center gap-1">
                    <Clock className="w-3 h-3" />
                    {video.duration}s
                  </span>
                  <span>{new Date(video.created_at).toLocaleDateString()}</span>
                </div>

                {/* Error Message */}
                {video.status === "failed" && video.error_message && (
                  <div className={`text-xs mb-3 p-2 rounded ${darkMode ? 'bg-red-900/20 text-red-400' : 'bg-red-50 text-red-600'}`}>
                    {video.error_message}
                  </div>
                )}

                {/* Actions */}
                <div className="flex gap-2">
                  {video.status === "pending" && (
                    <>
                      <Button
                        onClick={() => generateVideo(video.id)}
                        disabled={generatingIds.has(video.id)}
                        className="flex-1 bg-[#DC143C] hover:bg-[#b91031] text-white"
                        data-testid={`generate-video-${index}`}
                      >
                        {generatingIds.has(video.id) ? (
                          <>
                            <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                            Generating...
                          </>
                        ) : (
                          <>
                            <Sparkles className="w-4 h-4 mr-2" />
                            Generate
                          </>
                        )}
                      </Button>
                      <a 
                        href={`${process.env.REACT_APP_BACKEND_URL}/api/videos/${video.id}/download-package`}
                        download
                      >
                        <Button variant="outline" className={darkMode ? 'border-[#30363d] text-gray-300 hover:bg-[#21262d]' : ''} title="Download Script">
                          <FileText className="w-4 h-4" />
                        </Button>
                      </a>
                    </>
                  )}
                  
                  {video.status === "generating" && (
                    <Button disabled className="flex-1 bg-blue-600/50 text-white">
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Generating...
                    </Button>
                  )}
                  
                  {video.status === "completed" && video.video_url && (
                    <>
                      <Button
                        onClick={() => openPreview(video)}
                        variant="outline"
                        className={`flex-1 ${darkMode ? 'border-[#30363d] text-gray-300 hover:bg-[#21262d]' : ''}`}
                        data-testid={`preview-video-${index}`}
                      >
                        <Play className="w-4 h-4 mr-2" />
                        Preview
                      </Button>
                      <Button
                        onClick={() => regenerateVideo(video.id)}
                        disabled={generatingIds.has(video.id)}
                        variant="outline"
                        className={darkMode ? 'border-[#30363d] text-gray-300 hover:bg-[#21262d]' : ''}
                        title="Regenerate video with all new scenes"
                        data-testid={`regenerate-video-${index}`}
                      >
                        {generatingIds.has(video.id) ? (
                          <Loader2 className="w-4 h-4 animate-spin" />
                        ) : (
                          <RefreshCw className="w-4 h-4" />
                        )}
                      </Button>
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="outline" className={darkMode ? 'border-[#30363d] text-gray-300 hover:bg-[#21262d]' : ''} data-testid={`download-video-${index}`}>
                            <Download className="w-4 h-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end" className={darkMode ? 'bg-[#161b22] border-[#30363d]' : ''}>
                          <DropdownMenuItem asChild>
                            <a 
                              href={`${process.env.REACT_APP_BACKEND_URL}${video.video_url}`}
                              download
                              className={`flex items-center gap-2 cursor-pointer ${darkMode ? 'text-gray-300 hover:bg-[#21262d]' : ''}`}
                            >
                              <Film className="w-4 h-4" />
                              Download Video Only
                            </a>
                          </DropdownMenuItem>
                          <DropdownMenuItem asChild>
                            <a 
                              href={`${process.env.REACT_APP_BACKEND_URL}/api/videos/${video.id}/download-package`}
                              download
                              className={`flex items-center gap-2 cursor-pointer ${darkMode ? 'text-gray-300 hover:bg-[#21262d]' : ''}`}
                            >
                              <FileText className="w-4 h-4" />
                              Download Package (Video + Script)
                            </a>
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </>
                  )}
                  
                  {video.status === "failed" && (
                    <Button
                      onClick={() => generateVideo(video.id)}
                      disabled={generatingIds.has(video.id)}
                      className="flex-1 bg-red-600 hover:bg-red-700 text-white"
                      data-testid={`retry-video-${index}`}
                    >
                      <AlertCircle className="w-4 h-4 mr-2" />
                      Retry
                    </Button>
                  )}
                  
                  <Button
                    onClick={() => openPreview(video)}
                    variant="outline"
                    className={darkMode ? 'border-[#30363d] text-gray-300 hover:bg-[#21262d]' : ''}
                    data-testid={`view-script-${index}`}
                  >
                    <FileText className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Video Preview Dialog */}
      <Dialog open={previewOpen} onOpenChange={setPreviewOpen}>
        <DialogContent className={`max-w-4xl max-h-[90vh] overflow-y-auto ${darkMode ? 'bg-[#161b22] border-[#30363d] text-white' : ''}`} data-testid="video-dialog">
          <DialogHeader>
            <DialogTitle>{selectedVideo?.topic}</DialogTitle>
          </DialogHeader>
          
          {selectedVideo && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-4">
              {/* Video Player */}
              <div>
                {selectedVideo.status === "completed" && selectedVideo.video_url ? (
                  <video 
                    controls
                    className={`w-full rounded-xl aspect-[9/16] ${darkMode ? 'bg-black' : 'bg-gray-900'}`}
                    data-testid="video-player"
                  >
                    <source src={`${process.env.REACT_APP_BACKEND_URL}${selectedVideo.video_url}`} type="video/mp4" />
                  </video>
                ) : (
                  <div className={`aspect-[9/16] rounded-xl flex items-center justify-center ${darkMode ? 'bg-[#21262d]' : 'bg-gray-100'}`}>
                    <div className={`text-center ${darkMode ? 'text-gray-500' : 'text-gray-400'}`}>
                      <Film className="w-12 h-12 mx-auto mb-2" />
                      <p>Video not generated yet</p>
                    </div>
                  </div>
                )}
              </div>
              
              {/* Script Details */}
              <ScrollArea className="h-[500px] pr-4">
                <div className="space-y-4">
                  {selectedVideo.script && (
                    <div className={`p-4 rounded-xl ${darkMode ? 'bg-[#21262d]' : 'bg-gray-50'}`}>
                      <h4 className={`text-sm font-medium mb-3 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>Script</h4>
                      <p className={`whitespace-pre-wrap ${darkMode ? 'text-gray-200' : 'text-gray-700'}`}>{selectedVideo.script}</p>
                    </div>
                  )}
                  
                  {selectedVideo.voiceover_text && (
                    <div className={`p-4 rounded-xl ${darkMode ? 'bg-[#21262d]' : 'bg-gray-50'}`}>
                      <h4 className={`text-sm font-medium mb-3 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>Voiceover</h4>
                      <p className={darkMode ? 'text-gray-200' : 'text-gray-700'}>{selectedVideo.voiceover_text}</p>
                    </div>
                  )}
                  
                  {selectedVideo.visual_direction && (
                    <div className={`p-4 rounded-xl ${darkMode ? 'bg-[#21262d]' : 'bg-gray-50'}`}>
                      <h4 className={`text-sm font-medium mb-3 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>Visual Direction</h4>
                      <p className={darkMode ? 'text-gray-200' : 'text-gray-700'}>{selectedVideo.visual_direction}</p>
                    </div>
                  )}
                  
                  {selectedVideo.scene_breakdown?.length > 0 && (
                    <div className={`p-4 rounded-xl ${darkMode ? 'bg-[#21262d]' : 'bg-gray-50'}`}>
                      <h4 className={`text-sm font-medium mb-4 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>Scene Breakdown</h4>
                      <div className="space-y-3">
                        {selectedVideo.scene_breakdown.map((scene, index) => (
                          <div
                            key={scene.scene_number ? `scene-${scene.scene_number}` : `scene-${index}`}
                            className={`p-3 rounded-lg ${darkMode ? 'bg-[#161b22] border border-[#30363d]' : 'bg-white border border-gray-200'}`}
                          >
                            <div className="flex items-center gap-2 mb-2">
                              <Badge className="bg-[#DC143C]/10 text-[#DC143C] border-0">
                                Scene {scene.scene_number || index + 1}
                              </Badge>
                              {scene.duration && (
                                <span className={`text-xs ${darkMode ? 'text-gray-500' : 'text-gray-400'}`}>{scene.duration}s</span>
                              )}
                            </div>
                            {scene.visual && (
                              <p className={`text-sm mb-1 ${darkMode ? 'text-gray-300' : 'text-gray-600'}`}>
                                <span className={darkMode ? 'text-gray-500' : 'text-gray-400'}>Visual:</span> {scene.visual}
                              </p>
                            )}
                            {scene.text_overlay && (
                              <p className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                                <span className={darkMode ? 'text-gray-500' : 'text-gray-400'}>Text:</span> {scene.text_overlay}
                              </p>
                            )}
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              </ScrollArea>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
